import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, ArrowRight, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const contactInfo = [
  {
    icon: MapPin,
    title: "Our Office",
    lines: ["OFFICE NO 1009, 10th Floor, RK World Tower, Near Shital Park, Rajkot – 360006, Gujarat, India"],
    link: "https://www.google.com/maps/search/1317,+150+Feet+Ring+Rd,+Sheetal+Park,+Shastri+Nagar,+Dharam+Nagar+Society,+Rajkot,+Gujarat+360007",
    linkText: "View on Google Maps",
  },
  {
    icon: Phone,
    title: "Phone",
    lines: ["+91 75758 73232"],
    link: "tel:+917575873232",
    linkText: "Call Now",
  },
  {
    icon: Mail,
    title: "Email",
    lines: ["info@pranavlogistics.com"],
    link: "mailto:info@pranavlogistics.com?subject=Inquiry%20about%20services&body=Respected%20Sir%2FMa'am%2C%0A",
    linkText: "Send Email",
  },
  {
    icon: Clock,
    title: "Business Hours",
    lines: ["Monday - Saturday", "9:00 AM - 7:00 PM"],
  },
];

const serviceOptions = [
  "In-Land Transportation",
  "Customs Clearance",
  "Warehousing & Storage",
  "Ocean Freight",
  "Air Freight",
  "Project Cargo & Consultancy",
  "Third-Party Survey Services",
  "Import & Export Cargo",
  "Other",
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = "Name is required (min 2 characters)";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.email)) errs.email = "Please enter a valid email";
    if (!form.message.trim() || form.message.trim().length < 10) errs.message = "Message is required (min 10 characters)";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const text = `Hello Pranav Logistics,\nI would like to request a quote.\n\nName: ${form.name.trim()}\nEmail: ${form.email.trim()}\nPhone: ${form.phone.trim() || "Not provided"}\nService: ${form.service || "Not specified"}\nMessage: ${form.message.trim()}`;
    const url = `https://wa.me/917575873232?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  return (
    <div>
      {/* Page Header */}
      <section className="page-header py-20 md:py-28" data-testid="contact-page-header">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-['Barlow_Condensed'] font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-4" data-testid="contact-heading">
            Contact Us
          </h1>
          <p className="text-slate-300 text-base md:text-lg max-w-2xl mx-auto">
            Get in touch for a free quote or any logistics inquiry
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 md:py-24 bg-white" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, i) => (
              <div
                key={info.title}
                className="bg-[#F8FAFC] rounded-lg p-6 border border-slate-200 hover:border-[#0EA5E9] transition-colors"
                data-testid={`contact-info-card-${i}`}
              >
                <div className="bg-white p-3 rounded-lg w-fit mb-4 shadow-sm">
                  <info.icon size={24} strokeWidth={1.5} className="text-[#0EA5E9]" />
                </div>
                <h3 className="font-['Barlow_Condensed'] font-bold text-lg text-[#0F172A] mb-2">{info.title}</h3>
                {info.lines.map((line, j) => (
                  <p key={j} className="text-slate-500 text-sm">{line}</p>
                ))}
                {info.link && (
                  <a
                    href={info.link}
                    target={info.link.startsWith("http") ? "_blank" : undefined}
                    rel={info.link.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="text-[#F97316] text-sm font-semibold inline-flex items-center gap-1 mt-3 hover:gap-2 transition-all"
                  >
                    {info.linkText} <ArrowRight size={14} />
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* Form + Map */}
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <span className="text-[#F97316] font-semibold text-sm uppercase tracking-widest">Send Us a Message</span>
              <h2 className="font-['Barlow_Condensed'] font-bold text-3xl sm:text-4xl text-[#0F172A] mt-3 mb-6">
                Get a Free Quote
              </h2>
              <div className="section-divider mb-8" />
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Full Name *</label>
                    <Input
                      placeholder="John Doe"
                      value={form.name}
                      onChange={(e) => { setForm({ ...form, name: e.target.value }); setErrors({ ...errors, name: "" }); }}
                      className={`bg-white h-12 ${errors.name ? "border-red-400 focus:border-red-400" : "border-slate-300 focus:border-[#0EA5E9]"}`}
                      data-testid="contact-name-input"
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1" data-testid="contact-name-error">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Email Address *</label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={form.email}
                      onChange={(e) => { setForm({ ...form, email: e.target.value }); setErrors({ ...errors, email: "" }); }}
                      className={`bg-white h-12 ${errors.email ? "border-red-400 focus:border-red-400" : "border-slate-300 focus:border-[#0EA5E9]"}`}
                      data-testid="contact-email-input"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1" data-testid="contact-email-error">{errors.email}</p>}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Phone Number</label>
                    <Input
                      placeholder="+91 98765 43210"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="bg-white border-slate-300 focus:border-[#0EA5E9] h-12"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Service Required</label>
                    <Select value={form.service} onValueChange={(val) => setForm({ ...form, service: val })}>
                      <SelectTrigger className="bg-white border-slate-300 h-12" data-testid="contact-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceOptions.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Message *</label>
                  <Textarea
                    placeholder="Tell us about your logistics requirements..."
                    rows={5}
                    value={form.message}
                    onChange={(e) => { setForm({ ...form, message: e.target.value }); setErrors({ ...errors, message: "" }); }}
                    className={`bg-white ${errors.message ? "border-red-400 focus:border-red-400" : "border-slate-300 focus:border-[#0EA5E9]"}`}
                    data-testid="contact-message-input"
                  />
                  {errors.message && <p className="text-red-500 text-xs mt-1" data-testid="contact-message-error">{errors.message}</p>}
                </div>
                <button
                  type="submit"
                  className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  <Send size={18} /> Send via WhatsApp
                </button>
                <p className="text-slate-400 text-xs">Your details will be sent to our WhatsApp for a quick response</p>
              </form>
            </div>

            {/* Map */}
            <div>
              <span className="text-[#F97316] font-semibold text-sm uppercase tracking-widest">Find Us</span>
              <h2 className="font-['Barlow_Condensed'] font-bold text-3xl sm:text-4xl text-[#0F172A] mt-3 mb-6">
                Our Location
              </h2>
              <div className="section-divider mb-8" />
              <div className="map-container h-[400px] lg:h-[480px]" data-testid="google-map-embed">
                <iframe
                  src="https://www.google.com/maps?q=1317,+150+Feet+Ring+Rd,+Sheetal+Park,+Shastri+Nagar,+Dharam+Nagar+Society,+Rajkot,+Gujarat+360007,+India&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Pranav Logistics - RK World Tower, Rajkot"
                />
              </div>
              <p className="text-slate-500 text-sm mt-4">
                1317, 150 Feet Ring Rd, Sheetal Park, Shastri Nagar, Dharam Nagar Society, Rajkot, Gujarat 360007, India
              </p>
              <a
                href="https://www.google.com/maps/dir//1317,+150+Feet+Ring+Rd,+Sheetal+Park,+Shastri+Nagar,+Dharam+Nagar+Society,+Rajkot,+Gujarat+360007,+India"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-3 text-[#F97316] text-sm font-semibold hover:gap-3 transition-all"
                data-testid="map-get-directions-link"
              >
                <MapPin size={14} /> Get Directions on Google Maps <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
